import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts/permissions";
import { useGetChatChannelsQuery } from "../api";
import { ROCKETCHAT_BASE_URL } from "../consts/channelMap";

export function useChatPage() {
  const { t } = useTranslation("ChatPage");
  const { hasPermission, hasRole } = useAuth();
  const canManage = hasPermission(PERMISSIONS.CHAT.MANAGE_CHANNELS) || hasRole("chief");

  const { data: channels = [], isLoading, isFetching, isError, error, refetch } = useGetChatChannelsQuery(undefined, {
    refetchOnFocus: true,
  });

  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);
  const [isManageOpen, setIsManageOpen] = useState(false);

  const effectiveSelectedId = useMemo(() => {
    if (channels.length === 0) return null;
    if (selectedChannelId != null && channels.some((c) => c.id === selectedChannelId)) return selectedChannelId;
    return channels[0].id;
  }, [channels, selectedChannelId]);

  const selectedChannel = useMemo(() => {
    return channels.find((c) => c.id === effectiveSelectedId) ?? null;
  }, [channels, effectiveSelectedId]);

  const iframeUrl = selectedChannel
    ? `${ROCKETCHAT_BASE_URL}/${selectedChannel.name}?layout=embedded`
    : null;

  function handleSelectChannel(channelId: number) {
    setSelectedChannelId(channelId);
  }

  function openManage() {
    setIsManageOpen(true);
  }

  function closeManage() {
    setIsManageOpen(false);
  }

  return {
    t,
    channels,
    selectedChannelId: effectiveSelectedId,
    selectedChannel,
    iframeUrl,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    canManage,
    isManageOpen,
    openManage,
    closeManage,
    handleSelectChannel,
  };
}
